import { FilterProperties, NoneKey } from '@/models/Filter'
import { encodeQueryValue } from '@/utils/filterUtils'
import { clone } from '@/utils/tools'
import { useRoute, useRouter } from 'vue-router'

export function useFilter() {
  const route = useRoute()
  const router = useRouter()

  /**
   * Overwrites the value(s) for the specific filter key, but leaves the rest
   * @param key
   * @param values
   * @param previousFilterKey removes the previous filter with this key. used by custom filters where the key may change
   */
  const setFilter = async (key: string, values?: string[] | undefined, previousFilterKey?: string) => {
    const query = clone(route.query)
    if (previousFilterKey) {
      delete query[previousFilterKey]
    }

    query[key] = values?.map((value) => encodeQueryValue(value)).join(',') || null

    if (!query[key]) {
      delete query[key]
    }

    await router.push({ query })
  }

  // unlike setFilter, adds or removes value from query key (depending on state of enable) and navigates.
  const updateFilter = async (key: string, value: string, enable: boolean, event?: Event): Promise<boolean> => {
    const query = clone(route?.query || {})
    const values = new Set(query[key]?.toString().split(','))

    // const negatedValue = value.startsWith('!') ? value.slice(1) : `!${value}`
    // values.delete(WildcardKey)
    // values.delete(NoneKey)
    // values.delete(negatedValue)

    enable ? values.add(value) : values.delete(value)
    values.size ? (query[key] = Array.from(values).join(',')) : delete query[key]
    if (values.size) {
      query[key] = Array.from(values).join(',')
    } else {
      delete query[key]
    }

    const navFail = await router.push({ query })
    if (navFail) {
      event?.preventDefault()
    }
    return !navFail
  }

  const toggleAllFilters = async (key: string, options: FilterProperties[], toggleOn: boolean) => {
    const query = clone(route?.query || {})

    const queryKey = query[key]
    const activeOptions = Array.isArray(queryKey) ? queryKey : queryKey?.split(',')

    if (toggleOn) {
      const shouldBeActive = [activeOptions, options.map((option) => option.id)]
        .filter((o) => !!o)
        .flat()
        .filter((option) => option !== NoneKey)
      query[key] = shouldBeActive.join(',')
    } else {
      const optionIds = new Set(options.map((o) => o.id))
      query[key] = activeOptions?.filter((optionId) => optionId && !optionIds.has(optionId)).join(',') || null
    }

    if (!query[key]) {
      delete query[key]
    }

    await router.push({ query })
  }

  return {
    updateFilter,
    toggleAllFilters,
    setFilter,
  }
}
